import { useEffect, useState } from 'react';
import {
  createCapture,
  deleteCapture,
  listCaptures,
  type CaptureListEntry,
} from '../api-captures';
import { displayUrl } from '../api';
import {
  ensureNotificationPermission,
  useCaptureStream,
  type CapturedRequest,
} from '../hooks/useCaptureStream';
import { useStore } from '../store';

const ACTIVE_STORAGE_KEY = 'xsscool:captures:active';

function loadPersistedActiveId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(ACTIVE_STORAGE_KEY);
}

function persistActiveId(id: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (id) localStorage.setItem(ACTIVE_STORAGE_KEY, id);
    else localStorage.removeItem(ACTIVE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString();
}

function RequestRow({ req }: { req: CapturedRequest }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="capture-request">
      <button className="capture-request-head" onClick={() => setOpen((v) => !v)} type="button">
        <span className={`method method-${req.method.toLowerCase()}`}>{req.method}</span>
        <span className="capture-path">{req.path}</span>
        {req.query && <span className="capture-query">?{req.query}</span>}
        <span className="capture-time">{formatTime(req.receivedAt)}</span>
        <span className="chev">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="capture-request-body">
          {req.sourceIp && (
            <div className="capture-meta">
              <span>From:</span> <code>{req.sourceIp}</code>
            </div>
          )}
          <div className="capture-meta">
            <span>Headers:</span>
            <pre>{JSON.stringify(req.headers, null, 2)}</pre>
          </div>
          {req.body !== null && (
            <div className="capture-meta">
              <span>
                Body{req.bodyTruncated && <em> (truncated)</em>}:
              </span>
              <pre>{req.body}</pre>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function CapturesPanel() {
  const [captures, setCaptures] = useState<CaptureListEntry[]>([]);
  const [activeId, setActiveIdState] = useState<string | null>(loadPersistedActiveId);
  const setActiveId = (id: string | null) => {
    persistActiveId(id);
    setActiveIdState(id);
  };
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const requests = useCaptureStream(activeId);
  const notifyOnCapture = useStore((s) => s.notifyOnCapture);
  const setNotifyOnCapture = useStore((s) => s.setNotifyOnCapture);
  const resetUnread = useStore((s) => s.resetUnreadCaptures);

  useEffect(() => {
    resetUnread();
  }, [resetUnread]);

  const toggleNotify = async (next: boolean) => {
    if (next) {
      const perm = await ensureNotificationPermission();
      if (perm !== 'granted') {
        setNotifyOnCapture(false);
        return;
      }
    }
    setNotifyOnCapture(next);
  };

  const reload = async () => {
    const list = await listCaptures();
    setCaptures(list);
    if (list.length === 0) {
      setActiveId(null);
      return;
    }
    // Prefer the persisted selection if it still exists; otherwise pick first.
    const stillExists = activeId && list.some((c) => c.id === activeId);
    if (!stillExists) setActiveId(list[0]!.id);
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const c = await createCapture();
      setActiveId(c.id);
      await reload();
      window.dispatchEvent(new Event('xsscool:captures-changed'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteCapture(id);
    if (activeId === id) setActiveId(null);
    await reload();
    window.dispatchEvent(new Event('xsscool:captures-changed'));
  };

  const active = captures.find((c) => c.id === activeId);
  const domainBase = useStore((s) => s.domainBase);
  const domainSub = useStore((s) => s.domainSub);
  const captureUrl = active ? displayUrl(`/c/${active.id}`, { domainBase, domainSub }) : '';

  const copyUrl = () => {
    if (!captureUrl) return;
    navigator.clipboard.writeText(captureUrl).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  };

  return (
    <div className="captures-panel">
      <div className="captures-list">
        <button className="captures-create" onClick={handleCreate} disabled={creating} type="button">
          {creating ? 'Creating…' : '+ New capture endpoint'}
        </button>
        {captures.length === 0 && (
          <p className="hint">No capture endpoints yet. Create one, then point a request at its URL to see it here.</p>
        )}
        {captures.map((c) => (
          <div
            key={c.id}
            className={`captures-item ${c.id === activeId ? 'active' : ''}`}
          >
            <button onClick={() => setActiveId(c.id)} type="button" title={`/c/${c.id}`}>
              <span className="captures-item-label">{c.label || c.id}</span>
              <span className="captures-item-count" title="Captured requests">{c.requestCount}</span>
            </button>
            <button
              className="remove"
              onClick={() => void handleDelete(c.id)}
              aria-label="Delete capture"
              title="Delete"
              type="button"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div className="captures-detail">
        {active ? (
          <>
            <div className="capture-header">
              <strong>Capture URL</strong>
              <div className="capture-url-row">
                <input
                  readOnly
                  value={captureUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  aria-label="Capture URL"
                />
                <button
                  type="button"
                  className="capture-url-action"
                  onClick={copyUrl}
                  title="Copy URL to clipboard"
                  aria-label="Copy URL"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
                <a
                  className="capture-url-action"
                  href={captureUrl}
                  target="_blank"
                  rel="noreferrer"
                  title="Open the capture URL in a new tab (creates one captured GET)"
                >
                  Open ↗
                </a>
              </div>
              <div className="capture-meta-row">
                <span className="capture-meta-inline">
                  Expires {new Date(active.expiresAt).toLocaleString()}
                </span>
                <label className="checkbox notify-toggle" title="Show a browser notification on each new capture">
                  <input
                    type="checkbox"
                    checked={notifyOnCapture}
                    onChange={(e) => void toggleNotify(e.target.checked)}
                  />
                  Notify on capture
                </label>
              </div>
            </div>
            {requests.length === 0 ? (
              <p className="hint">
                Waiting for requests… try{' '}
                <code>curl -X POST {captureUrl} -d 'hi'</code>
              </p>
            ) : (
              <ul className="capture-requests">
                {requests.map((r) => (
                  <RequestRow key={r.id} req={r} />
                ))}
              </ul>
            )}
          </>
        ) : (
          <p className="hint">Create a capture endpoint to see incoming requests live.</p>
        )}
      </div>
    </div>
  );
}
